import React, { useEffect, useRef, useState, useContext } from 'react'
import ChatMessages from './chat-history/ChatMessages'
import { ChatInput } from './ChatInput'
import { useAliContextBar } from './ContextChip'
import { useDispatch, useSelector } from 'react-redux'
import { setAliOpenSideBar } from '../../../_redux/actions/chatbotActions'
import { subscribeAliIntake } from './frontend-tools/fdc3Bridge'
import { ChatbotContext } from './context/ChatbotProvider'
import { Row, Spinner } from 'reactstrap'
import { formatChatMessages } from './utils/TextUtils'
import { checkIsDrawerMode } from './utils/helpers'
import { getRandomGreeting } from './utils/constants'

export const ChatRoom: React.FC<{
  isLoadingAgents: boolean
  onNewConversationStart: () => void
}> = ({ isLoadingAgents, onNewConversationStart }) => {
  const {
    fetchingMsgs,
    messages,
    rawMessages,
    setMessages,
    isStreaming,
    setDisableInput,
    selectedMessage
  } = useContext(ChatbotContext)
  const documentFileRef = useRef<HTMLInputElement | null>(null)
  const inputFileRef = useRef<HTMLTextAreaElement | null>(null)
  const dispatch = useDispatch()
  const { isFullScreen } = useSelector((state: any) => state.chatbotReducer)
  const profileInfo = useSelector((state: any) => state?.profileReducer?.profile)
  const [promptText, setPromptText] = useState('')
  const [totalMsgCount] = useState(0)
  const chatRoomRef = useRef<HTMLDivElement | null>(null)
  const isDrawerMode = checkIsDrawerMode()
  // Horizontal inset applied as padding on the scroll container (not margin on the outer
  // wrapper) so the scrollable element spans the full width and the wheel works everywhere,
  // including the side whitespace. The width factor keeps the composer's responsive breakpoint
  // (parentContainerWidth) identical to the previous margin-based layout.
  const contentInsetClass = selectedMessage
    ? 'px-[2%]'
    : isDrawerMode && !isFullScreen
    ? 'px-1'
    : 'px-[10%]'
  const [chatRoomWidth, setChatRoomWidth] = useState(0)
  const [greetingMessage, setGreetingMessage] = useState('')
  // Drives hiding the message list / stretching the composer row when the input is expanded
  const [isComposerExpanded, setIsComposerExpanded] = useState(false)
  // Single source of truth for the Ali context bar: drives both its rendering and
  // the composer's top-edge flattening so the two read as one continuous frame.
  const contextBar = useAliContextBar()

  useEffect(() => {
    setGreetingMessage(getRandomGreeting())
  }, [])

  // "Ask Ali" (Spec 010 §9): when a workspace app raises Mandg.AskAli, open the
  // drawer and pre-fill the input — never auto-send (prompt-injection safeguard).
  useEffect(
    () =>
      subscribeAliIntake((request) => {
        dispatch(setAliOpenSideBar(true))
        setPromptText(request.prompt)
      }),
    [dispatch]
  )

  useEffect(() => {
    // ResizeObserver instead of a window 'resize' listener - the container can resize without the window resizing (side-panel opening, spinner -> content swap).
    const node = chatRoomRef.current
    if (!node) return

    const updateWidth = () => setChatRoomWidth(node.getBoundingClientRect().width)
    updateWidth() // Get initial width

    const observer = new ResizeObserver(updateWidth)
    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setDisableInput(isStreaming)
  }, [isStreaming])

  useEffect(() => {
    setPromptText('')
    if (rawMessages?.length > 0) {
      setMessages((prev) => formatChatMessages(rawMessages, prev, false, prev.length === 0))
    } else {
      setMessages([])
    }
  }, [rawMessages])

  const renderChatInput = () => {
    return (
      <div className="flex flex-col items-center w-full h-full">
        {contextBar.node}
        <ChatInput
          documentFileRef={documentFileRef}
          inputFileRef={inputFileRef}
          promptText={promptText}
          setPromptText={setPromptText}
          parentContainerWidth={chatRoomWidth}
          isLoadingAgents={isLoadingAgents}
          hasContextBar={contextBar.visible}
          onNewConversationStart={onNewConversationStart}
          isExpanded={isComposerExpanded}
          onExpandedChange={setIsComposerExpanded}
        />
      </div>
    )
  }

  // Shared 3-row grid for both states so ChatInput never remounts when hasMessages flips
  const hasMessages = messages?.length > 0
  // Suppress the grid-row transition for one paint when hasMessages flips, so it doesn't fight the instant flex-direction swap below
  const prevHasMessagesRef = useRef(hasMessages)
  const [suppressRowTransition, setSuppressRowTransition] = useState(false)
  useEffect(() => {
    if (prevHasMessagesRef.current !== hasMessages) {
      prevHasMessagesRef.current = hasMessages
      setSuppressRowTransition(true)
      // Re-enable the transition on the next frame, once this flip has painted
      const frame = requestAnimationFrame(() => setSuppressRowTransition(false))
      return () => cancelAnimationFrame(frame)
    }
  }, [hasMessages])
  // row0/row2 are spacers that shrink to 0 so row1 (greeting/composer) fills the space; 999fr on row1 does the same when the composer is expanded.
  const gridRows = !hasMessages
    ? isComposerExpanded
      ? 'grid-rows-[minmax(0,0fr)_minmax(min-content,999fr)_minmax(0,0fr)]'
      : 'grid-rows-[minmax(0,1fr)_minmax(min-content,0fr)_minmax(0,1fr)]'
    : isComposerExpanded
    ? 'grid-rows-[minmax(0,0fr)_minmax(min-content,999fr)_minmax(0,0fr)]'
    : 'grid-rows-[minmax(0,999fr)_minmax(min-content,1fr)_minmax(0,0fr)]'

  return (
    <div ref={chatRoomRef} className="overflow-hidden h-full">
      {fetchingMsgs ? (
        <div className="flex items-center justify-center h-full px-1">
          <Row className="spinner-wrapper">
            <Spinner color="primary" />
            Loading chat...
          </Row>
        </div>
      ) : (
        <div
          className={`grid h-full ${
            suppressRowTransition ? '' : 'transition-[grid-template-rows] duration-300 ease-in-out'
          } ${hasMessages ? '' : 'px-1'} ${gridRows}`}>
          <div
            className={`chat-fade-container relative min-h-0 overflow-hidden transition-opacity duration-300 ease-in-out ${
              hasMessages && !isComposerExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            aria-hidden={!hasMessages || isComposerExpanded}>
            {hasMessages && (
              <>
                <div
                  className={`${contentInsetClass} transition-[padding] duration-300 ease h-full !overflow-y-scroll !overflow-x-auto overscroll-contain chat-scroll-h-visible`}>
                  <ChatMessages totalMsgCount={totalMsgCount} />
                </div>
                <div className="chat-fade-overlay absolute bottom-0 left-0 right-0 z-10 pointer-events-none" />
              </>
            )}
          </div>
          <div
            className={
              hasMessages
                ? 'flex justify-center items-stretch px-2 input pt-2 mb-2 min-h-0'
                : 'min-h-0 overflow-hidden flex flex-col items-center gap-2 w-full'
            }>
            <h3 className={`theme-text-item flex-shrink-0 ${hasMessages ? 'hidden' : ''}`}>
              {greetingMessage ? `Hi ${profileInfo?.givenName}, ${greetingMessage}` : ''}
            </h3>
            <div className="flex-1 min-h-0 w-full flex flex-col items-center pb-2">
              {renderChatInput()}
            </div>
          </div>
          <div />
        </div>
      )}
    </div>
  )
}
